import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Select from 'react-select';
import Modal from './Modal';
import './GeraBPA.css';
import apiService from '../services/apiService';
import { BarChart3, CheckCircle2, Search, Trash2 } from 'lucide-react';

const GeraBPA = () => {
  const { token } = useAuth();
  
  // Estados para filtros
  const [filtros, setFiltros] = useState({
    competencia: '',
    unidade: null,
    sigla: ''
  });

  // Estados para dados
  const [dadosTabela, setDadosTabela] = useState([]);
  const [loadingTabela, setLoadingTabela] = useState(false);
  const [totalRegistros, setTotalRegistros] = useState(0);

  // Estados para paginação
  const [paginacao, setPaginacao] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });

  // Estados para modal
  const [modal, setModal] = useState({
    isOpen: false,
    type: 'confirm',
    title: '',
    message: '',
    onConfirm: null,
    onCancel: null,
    confirmText: 'Confirmar',
    cancelText: 'Cancelar'
  });

  // Estado para loading da geração do BPA
  const [isGerandoBPA, setIsGerandoBPA] = useState(false);
  const [progressoBPA, setProgressoBPA] = useState(0);
  const [mensagemProgresso, setMensagemProgresso] = useState('');
  const [resultadoBPA, setResultadoBPA] = useState(null);


  // Estados para opções dos selects
  const [opcoesUnidades, setOpcoesUnidades] = useState([]);
  const [loadingUnidades, setLoadingUnidades] = useState(false);

  // Carregar unidades ao montar o componente
  useEffect(() => {
    carregarUnidades();
  }, []);

  // Função para carregar unidades
  const carregarUnidades = async () => {
    if (!token) return;

    setLoadingUnidades(true);
    try {
      const result = await apiService.authenticatedRequest('/api/queries/unidades', {
        method: 'GET'
      });

      if (result.success) {
        const opcoes = result.data.map(item => ({
          value: item.value,
          label: item.label
        }));
        setOpcoesUnidades(opcoes);
      } else {
        console.error('Erro ao carregar unidades:', result.error);
      }
    } catch (error) {
      console.error('Erro ao carregar unidades:', error);
    } finally {
      setLoadingUnidades(false);
    }
  };

  // Função para sanitizar filtros (remover referências circulares)
  const sanitizarFiltros = (filtros) => {
    const filtrosLimpos = {
      competencia: '',
      unidade: null,
      sigla: ''
    };
    
    try {
      // Processar competência
      if (typeof filtros.competencia === 'string') {
        filtrosLimpos.competencia = filtros.competencia;
      }
      
      // Processar unidade
      if (filtros.unidade && typeof filtros.unidade === 'object' && filtros.unidade !== null) {
        try {
          const unidadeValue = filtros.unidade.value;
          const unidadeLabel = filtros.unidade.label;
          if (unidadeValue !== undefined && unidadeValue !== null && 
              unidadeLabel !== undefined && unidadeLabel !== null) {
            filtrosLimpos.unidade = {
              value: String(unidadeValue),
              label: String(unidadeLabel)
            };
          }
        } catch (e) {
          console.warn('Erro ao processar unidade:', e);
        }
      }
      
      // Processar sigla
      if (typeof filtros.sigla === 'string') {
        filtrosLimpos.sigla = filtros.sigla;
      }
      
    } catch (error) {
      console.error('Erro ao sanitizar filtros:', error);
      return {
        competencia: '',
        unidade: null,
        sigla: ''
      };
    }
    
    return filtrosLimpos;
  };

  // Função para buscar dados
  const handleBuscar = async (page = 1) => {
    if (!token) {
      console.error('Token de autenticação não encontrado');
      return;
    }

    setLoadingTabela(true);
    
    try {
      const filtrosSanitizados = sanitizarFiltros(filtros);
      
      const filtrosComPaginacao = {
        ...filtrosSanitizados,
        page: Number(page),
        limit: Number(paginacao.limit)
      };

      // Verificar se o objeto pode ser serializado
      let jsonBody;
      try {
        jsonBody = JSON.stringify(filtrosComPaginacao);
      } catch (jsonError) {
        console.error('Erro ao serializar filtros:', jsonError);
        window.alert('Erro interno: não foi possível processar os filtros. Tente novamente.');
        return;
      }

      const result = await apiService.authenticatedRequest('/api/bpa/cabecalhos', {
        method: 'POST',
        body: jsonBody
      });

      if (result.success) {
        setDadosTabela(result.data);
        setTotalRegistros(result.pagination.total);
        setPaginacao(result.pagination);
      } else {
        setDadosTabela([]);
        setTotalRegistros(0);
        setPaginacao({
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0,
          hasNext: false,
          hasPrev: false
        });
        window.alert(`Erro ao buscar dados: ${result.error}`);
      }
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      setDadosTabela([]);
      setTotalRegistros(0);
      setPaginacao({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      });
      window.alert('Erro de conexão com o servidor.');
    } finally {
      setLoadingTabela(false);
    }
  };

  // Função para limpar filtros
  const handleLimpar = () => {
    setFiltros({
      competencia: '',
      unidade: null,
      sigla: ''
    });
    setDadosTabela([]);
    setTotalRegistros(0);
    setPaginacao({
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
      hasNext: false,
      hasPrev: false
    });
  };

  // Funções de paginação
  const handlePaginaAnterior = () => {
    if (paginacao.hasPrev) {
      handleBuscar(paginacao.page - 1);
    }
  };

  const handleProximaPagina = () => {
    if (paginacao.hasNext) {
      handleBuscar(paginacao.page + 1);
    }
  };

  const handleIrParaPagina = (pagina) => {
    if (pagina >= 1 && pagina <= paginacao.totalPages) {
      handleBuscar(pagina);
    }
  };

  // Função para abrir modal
  const openModal = (type, title, message, onConfirm, onCancel = null, confirmText = 'Confirmar', cancelText = 'Cancelar') => {
    setModal({
      isOpen: true,
      type,
      title,
      message,
      onConfirm,
      onCancel,
      confirmText,
      cancelText
    });
  };

  // Função para fechar modal
  const closeModal = () => {
    setModal(prev => ({ ...prev, isOpen: false }));
  };

  // Função para simular progresso da geração do BPA
  const simularProgresso = () => {
    setProgressoBPA(0);
    setMensagemProgresso('Iniciando geração...');
    
    const etapas = [
      { progresso: 20, mensagem: 'Consultando dados da unidade...' },
      { progresso: 40, mensagem: 'Processando registros consolidados...' },
      { progresso: 60, mensagem: 'Gerando registros individualizados...' },
      { progresso: 80, mensagem: 'Calculando validações...' },
      { progresso: 95, mensagem: 'Finalizando arquivo...' },
      { progresso: 100, mensagem: 'Arquivo gerado com sucesso!' }
    ];

    etapas.forEach((etapa, index) => {
      setTimeout(() => {
        setProgressoBPA(etapa.progresso);
        setMensagemProgresso(etapa.mensagem);
      }, index * 600); // 600ms entre cada etapa
    });
  };

      // Função para gerar BPA
      const handleGerarBPA = (registro) => {
        openModal(
          'confirm',
          'Gerar BPA',
          `Deseja gerar o BPA para a unidade "${registro.unidade}"?`,
          async () => {
            closeModal(); // Fechar modal de confirmação
            setIsGerandoBPA(true);
            setResultadoBPA(null);
            simularProgresso(); // Iniciar simulação de progresso
            try {
              const result = await apiService.authenticatedRequest(`/api/bpa/cabecalhos/${registro.id}/gerar-bpa`, {
                method: 'POST'
              });

          if (result.success) {
            setResultadoBPA(result.data);
            // Fazer download imediatamente
            if (result.data.arquivoBase64) {
              try {
                // Converter base64 para blob no navegador
                const byteCharacters = atob(result.data.arquivoBase64);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                  byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'text/plain;charset=utf-8' });
                
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = result.data.nomeArquivo;
                a.style.display = 'none';
                document.body.appendChild(a);
                
                // Tentar múltiplas abordagens para o download
                try {
                  a.click();
                } catch (clickError) {
                  console.warn('Erro no click, tentando abordagem alternativa:', clickError);
                  // Abordagem alternativa
                  const event = new MouseEvent('click', {
                    view: window,
                    bubbles: true,
                    cancelable: true
                  });
                  a.dispatchEvent(event);
                }
                
                // Limpar recursos
                setTimeout(() => {
                  window.URL.revokeObjectURL(url);
                  document.body.removeChild(a);
                }, 1000);
              } catch (downloadError) {
                console.error('Erro no download:', downloadError);
                openModal(
                  'error',
                  'Erro no Download',
                  `Arquivo gerado com sucesso, mas houve erro no download.\n\nErro: ${downloadError.message}\n\nVerifique as configurações do navegador e se os downloads automáticos estão permitidos.`,
                  null,
                  null,
                  'OK'
                );
              }
            } else {
              openModal(
                'error',
                'Arquivo Não Disponível',
                'Arquivo gerado, mas não foi possível obter o conteúdo para download.',
                null,
                null,
                'OK'
              );
            }
          } else {
            openModal(
              'error',
              'Erro ao Gerar BPA',
              result.error,
              null,
              null,
              'OK'
            );
          }
            } catch (error) {
              console.error('Erro ao gerar BPA:', error);
              setIsGerandoBPA(false);
              setProgressoBPA(0);
              setMensagemProgresso('');
              openModal(
                'error',
                'Erro de Conexão',
                'Erro de conexão ao gerar BPA. Verifique sua conexão e tente novamente.',
                null,
                null,
                'OK'
              );
            }
          },
          null,
          'Gerar BPA',
          'Cancelar'
        );
      };

  // Função para deletar
  const handleDeletar = (registro) => {
    openModal(
      'confirm',
      'Deletar Registro',
      `Tem certeza que deseja deletar o registro da unidade "${registro.unidade}"?\n\nEsta ação não pode ser desfeita.`,
      async () => {
        try {
          const result = await apiService.authenticatedRequest(`/api/bpa/cabecalhos/${registro.id}`, {
            method: 'DELETE'
          });

          if (result.success) {
            openModal(
              'success',
              'Registro Deletado',
              'Registro deletado com sucesso!',
              () => {
                handleBuscar(paginacao.page); // Recarregar a página atual
              },
              null,
              'OK'
            );
          } else {
            openModal(
              'error',
              'Erro ao Deletar',
              result.error,
              null,
              null,
              'OK'
            );
          }
        } catch (error) {
          console.error('Erro ao deletar:', error);
          openModal(
            'error',
            'Erro de Conexão',
            'Erro de conexão ao deletar registro. Verifique sua conexão e tente novamente.',
            null,
            null,
            'OK'
          );
        }
      },
      null,
      'Deletar',
      'Cancelar'
    );
  };

  // Função para formatar data
  const formatarData = (dataString) => {
    if (!dataString || dataString === 'N/A') return 'N/A';
    
    try {
      const data = new Date(dataString);
      return data.toLocaleString('pt-BR');
    } catch (error) {
      console.warn('Erro ao formatar data:', error);
      return dataString;
    }
  };

  return (
    <div className="gera-bpa">
      {/* Loading overlay quando está gerando BPA */}
      {isGerandoBPA && (
        <div className="loading-overlay">
          <div className="loading-content">
            {resultadoBPA ? (
              // Tela de sucesso
              <div className="success-content">
                <div className="success-icon flex justify-center text-emerald-600">
                  <CheckCircle2 size={48} />
                </div>
                <h3>BPA Gerado com Sucesso!</h3>
                <div className="result-info">
                  <p><strong>Arquivo:</strong> {resultadoBPA.nomeArquivo}</p>
                  <p><strong>Total de registros processados:</strong> {resultadoBPA.totalRegistros?.toLocaleString('pt-BR')}</p>
                  <p><strong>Tamanho do arquivo:</strong> {resultadoBPA.arquivoBase64 ? Math.round(resultadoBPA.arquivoBase64.length * 0.75 / 1024) + ' KB' : 'N/A'}</p>
                </div>
                <p className="success-message">O arquivo foi baixado automaticamente!</p>
                <button 
                  className="btn-close-loading"
                  onClick={() => {
                    setIsGerandoBPA(false);
                    setProgressoBPA(0);
                    setMensagemProgresso('');
                    setResultadoBPA(null);
                  }}
                >
                  Fechar
                </button>
              </div>
            ) : (
              // Tela de carregamento
              <div className="progress-content">
                <div className="loading-spinner-large">
                  <div className="spinner"></div>
                </div>
                <h3>Gerando BPA...</h3>
                <p className="progress-message">{mensagemProgresso}</p>
                
                {/* Barra de progresso */}
                <div className="progress-bar-container">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${progressoBPA}%` }}
                    ></div>
                  </div>
                  <div className="progress-percentage">{progressoBPA}%</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="content-header">
        <h1>Cabeçalho</h1>
      </div>

      {/* Filtros */}
      <div className="filtros-section">
        <div className="filtros-grid">
          <div className="filtro-item">
            <label>Competência:</label>
            <input
              type="text"
              value={filtros.competencia}
              onChange={(e) => setFiltros({ ...filtros, competencia: e.target.value })}
              placeholder="Ex: 202412"
              className="filtro-input"
            />
          </div>

          <div className="filtro-item">
            <label>Unidade:</label>
            <Select
              value={filtros.unidade}
              onChange={(opcao) => setFiltros({ ...filtros, unidade: opcao })}
              options={opcoesUnidades}
              placeholder="Selecione uma unidade"
              isClearable
              isLoading={loadingUnidades}
              className="react-select"
              classNamePrefix="react-select"
            />
          </div>

          <div className="filtro-item">
            <label>Sigla:</label>
            <input
              type="text"
              value={filtros.sigla}
              onChange={(e) => setFiltros({ ...filtros, sigla: e.target.value })}
              placeholder="Ex: 944557"
              className="filtro-input"
            />
          </div>
        </div>

        <div className="filtros-actions">
          <button 
            className="btn btn-primary" 
            onClick={() => handleBuscar(1)}
            disabled={loadingTabela}
          >
            <Search size={16} />
            Buscar
          </button>
          <button 
            className="btn btn-secondary" 
            onClick={handleLimpar}
            disabled={loadingTabela}
          >
            Limpar
          </button>
        </div>
      </div>

      {/* Resultados */}
      <div className="resultados-section">
        <div className="table-info">
          <span className="total-registros">
            {totalRegistros > 0 ? `${totalRegistros} registros encontrados` : 'Nenhum registro encontrado'}
          </span>
        </div>

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th width="80">Ações</th>
                <th>CMPT</th>
                <th>Sigla</th>
                <th>Unidade</th>
                <th>Responsável</th>
                <th>CNPJ</th>
                <th>Sec. Destino</th>
                <th>Orgão Dest.</th>
                <th>Geração</th>
              </tr>
            </thead>
            <tbody>
              {loadingTabela ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', padding: '20px' }}>
                    <div className="loading-spinner">
                      <div className="spinner"></div>
                      <span>Carregando dados...</span>
                    </div>
                  </td>
                </tr>
              ) : dadosTabela.length > 0 ? (
                dadosTabela.map((item, index) => (
                  <tr key={index}>
                    <td className="actions-cell">
                      <button 
                        className="btn-action btn-generate" 
                        onClick={() => handleGerarBPA(item)}
                        title="Gera BPA"
                        disabled={isGerandoBPA}
                      >
                        {isGerandoBPA ? (
                          <span className="loading-spinner-small"></span>
                        ) : (
                          <BarChart3 size={16} />
                        )}
                      </button>
                      <button 
                        className="btn-action btn-delete" 
                        onClick={() => handleDeletar(item)}
                        title="Deletar"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                    <td>{item.cmpt || 'N/A'}</td>
                    <td>{item.sigla || 'N/A'}</td>
                    <td>{item.unidade || 'N/A'}</td>
                    <td>{item.responsavel || 'N/A'}</td>
                    <td>{item.cnpj || 'N/A'}</td>
                    <td>{item.sec_destino || 'N/A'}</td>
                    <td>{item.orgao_dest || 'N/A'}</td>
                    <td>{formatarData(item.geracao)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="no-data">
                    <div>
                      <span>Nenhum registro encontrado</span>
                      <p>Clique em "Buscar" para carregar os dados</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        {paginacao.totalPages > 1 && (
          <div className="paginacao">
            <div className="pagination-info">
              Página {paginacao.page} de {paginacao.totalPages}
            </div>
            
            <div className="pagination-controls">
              <button 
                className="pagination-btn" 
                onClick={handlePaginaAnterior}
                disabled={!paginacao.hasPrev}
              >
                Anterior
              </button>
              
              {/* Números das páginas */}
              {Array.from({ length: Math.min(10, paginacao.totalPages) }, (_, i) => {
                const pageNum = i + 1;
                const isCurrentPage = pageNum === paginacao.page;
                
                return (
                  <button
                    key={pageNum}
                    className={`page-number ${isCurrentPage ? 'active' : ''}`}
                    onClick={() => handleIrParaPagina(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
              
              {paginacao.totalPages > 10 && (
                <>
                  <span className="pagination-ellipsis">...</span>
                  <button
                    className="page-number"
                    onClick={() => handleIrParaPagina(paginacao.totalPages)}
                  >
                    {paginacao.totalPages}
                  </button>
                </>
              )}
              
              <button 
                className="pagination-btn" 
                onClick={handleProximaPagina}
                disabled={!paginacao.hasNext}
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Confirmação */}
      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        onConfirm={modal.onConfirm}
        onCancel={modal.onCancel}
        confirmText={modal.confirmText}
        cancelText={modal.cancelText}
      />
    </div>
  );
};

export default GeraBPA;
